"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import MuiLink from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { FunnelButton } from "../../FunnelButton";
import { QueueHeadline } from "./QueueHeadline";
import { getUrls } from "@portal/app/_common/lib/urls";

interface RedirectScreenProps {
  title: string;
  body: string;
  isBodyMarkdown: boolean;
  redirectUrl: string;
  redirectDelaySeconds?: number;
  /** When set, replaces the default "Open application now" CTA copy. */
  ctaTextOverride?: string;
}

export const RedirectScreen = ({
  title,
  body,
  isBodyMarkdown,
  redirectUrl,
  redirectDelaySeconds,
  ctaTextOverride,
}: RedirectScreenProps) => {
  const countdownActive = !!redirectDelaySeconds && redirectDelaySeconds > 0;
  const [remaining, setRemaining] = useState<number | null>(
    countdownActive ? redirectDelaySeconds! : null,
  );
  const [cancelled, setCancelled] = useState(false);
  const redirectedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const performRedirect = useCallback(() => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    setRemaining(null);
    window.location.href = getUrls().LeadRedirect(redirectUrl);
  }, [redirectUrl]);

  const handleCancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setCancelled(true);
    setRemaining(null);
  }, []);

  // Single-shot timer for the actual navigation. Kept independent of the display
  // interval so render-cycle overhead never lets the redirect drift past the
  // configured delay.
  useEffect(() => {
    if (!countdownActive || cancelled) return;
    timeoutRef.current = setTimeout(performRedirect, redirectDelaySeconds! * 1000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [countdownActive, cancelled, redirectDelaySeconds, performRedirect]);

  // Decrement the visible countdown every second. Drift here only affects the
  // displayed number, not when the redirect fires.
  useEffect(() => {
    if (!countdownActive || cancelled) return;
    const interval = setInterval(() => {
      setRemaining((r) => (r !== null && r > 0 ? r - 1 : r));
    }, 1000);
    return () => clearInterval(interval);
  }, [countdownActive, cancelled]);

  const ctaText = ctaTextOverride ?? "Open application now";
  const showCountdown = countdownActive && !cancelled && remaining !== null;
  const progressPercent = showCountdown
    ? ((redirectDelaySeconds! - remaining) / redirectDelaySeconds!) * 100
    : 0;

  return (
    <>
      <QueueHeadline title={title} body={body} isMarkdown={isBodyMarkdown} />
      <Box sx={{ mt: 1, width: "100%" }}>
        <FunnelButton
          component="a"
          href={getUrls().LeadRedirect(redirectUrl)}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            performRedirect();
          }}
          data-testid="success-cta"
          sx={{ position: "relative" }}
        >
          {ctaText}
          {showCountdown && (
            <Box
              sx={{
                position: "absolute",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
              }}
              aria-live="polite"
              data-testid="redirect-countdown"
            >
              <CircularProgress
                variant="determinate"
                value={progressPercent}
                size={28}
                thickness={4}
                sx={{ color: "common.white", position: "absolute" }}
              />
              <Typography
                variant="caption"
                sx={{ color: "common.white", fontWeight: 700, lineHeight: 1 }}
              >
                {remaining}
              </Typography>
            </Box>
          )}
        </FunnelButton>
      </Box>
      {countdownActive && !cancelled && (
        <MuiLink
          component="button"
          type="button"
          onClick={handleCancel}
          underline="always"
          data-testid="success-stay-on-page"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          Stay on this page
        </MuiLink>
      )}
    </>
  );
};
